import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpHeaders, HttpRequest, HttpResponseBase } from "@angular/common/http";
import { inject, Injector } from "@angular/core";
import { environment } from "environments/environment";
import { catchError, mergeMap, Observable, of, throwError } from "rxjs";
import { AuthService } from "../auth/auth.service";
import { toLogin, tryRefreshToken } from "./refresh-token";
import { ApiErrorService } from "../../_services/api-error.service";
import { TelemetryService } from "../telemetry/telemetry.service";

export const defaultInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const injector = inject(Injector);
  const apiErrorService: ApiErrorService = inject(ApiErrorService);
  const telemetryService: TelemetryService = inject(TelemetryService);

  // set additional header eg: Authorization, Accept-Language etc
  const getAdditionalHeaders = (url: string, headers?: HttpHeaders): { [name: string]: string } => {
    const res: { [name: string]: string } = {};
    const token = authService.accessToken;
    if (token && !url.endsWith("Identity/refresh")) {
      res["Authorization"] = `Bearer ${token}`;
    }
    return res;
  };

  // Browser HTTP cache keys GET responses by URL, not by Authorization header - force revalidation so a
  // refreshed token/scope is never masked by a stale cached response.
  const withNoCacheHeaders = (req: HttpRequest<unknown>): HttpRequest<unknown> =>
    req.clone({
      setHeaders: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

  // data | error handler
  const handleData = (ev: HttpResponseBase, req: HttpRequest<any>, next: HttpHandlerFn): Observable<any> => {
    switch (ev.status) {
      case 200:
        break;
      // authentication error
      case 401:
        // A failed sign-in is bad credentials, not an expired session; let the sign-in page show it.
        if (req.url.includes("Identity/login")) {
          break;
        }
        // get new access token with refresh token
        if (authService.refreshToken && !req.url.endsWith("Identity/refresh")) {
          return tryRefreshToken(injector, ev, req, next);
        }
        toLogin(injector);
        break;
      // access denied
      case 403:
      // endpoint not found
      case 404:
      // backend APIs error
      case 500:
      case 422:
        if (ev instanceof HttpErrorResponse) {
          if (ev.error?.type === "application/problem+json") {
            apiErrorService.handleResponse(ev);
          }
        }
        break;
    }
    if (ev instanceof HttpErrorResponse) {
      return throwError(() => ev);
    } else {
      return of(ev);
    }
  };

  // check if is a local request
  const isLocalAssets = (path: string) => {
    // subdirectories in assets
    const subdirectories = ["core", "data", "fonts", "i18n", "icons", "images", "styles"];
    return subdirectories.some(subdir => path.includes(`assets/${subdir}`));
  };

  /* apply API base URL to the request */
  let url = req.url;
  const wasLocalAsset = isLocalAssets(url);
  if (!url.startsWith("https://") && !url.startsWith("http://")) {
    // if request is for local assets or call mock API, keep it. otherwise apply the api base url
    if (!wasLocalAsset && !url.startsWith("api/")) {
      let baseUrl = localStorage.getItem("serverLocationUrl") || environment.baseURL;
      url = `${baseUrl}/${url.startsWith("/") ? url.substring(1) : url}`;
    }
  }
  /* apply authentication Bearer token */
  let newReq = req.clone({
    url,
    setHeaders: getAdditionalHeaders(url, req.headers),
  });
  // Local assets stay cacheable; API responses carry per-caller data.
  if (!wasLocalAsset) {
    newReq = withNoCacheHeaders(newReq);
  }
  // do request
  return telemetryService.traceRequest(newReq, next).pipe(
    mergeMap(ev => {
      if (ev instanceof HttpResponseBase) {
        return handleData(ev, newReq, next);
      }
      return of(ev);
    }),
    catchError((err: HttpErrorResponse) => handleData(err, newReq, next))
  );
};
